// ============================================
// FAQ 管理页面（/faq/uadmin）
// ============================================

import { useState, useEffect } from 'react';
import {
  Search, Plus, Filter, ChevronRight, ChevronUp, ChevronDown,
  Edit3, Trash2, Eye, LayoutGrid, List as ListIcon,
  Loader2, RefreshCw, Lock,
} from 'lucide-react';
import { useFaqStore } from './faq-store';
import FaqEditorModal from './FaqEditorModal';
import FaqExpandedView from './FaqExpandedView';
import { useEditingLock } from './use-editing-lock';
import { faqAnswerToPlainText } from './faq-rich-content';
import { faqTagLabels } from './faq-tag-utils';
import type { FaqItem } from './types';

export default function FaqAdminPage() {
  const {
    items, loading, error,
    searchQuery, primaryTagFilter, secondaryTagFilter,
    expandedIds,
    fetchItems, fetchTags, fetchTagOrders,
    deleteItem, reorderItemsInTag,
    setSearchQuery, setPrimaryTagFilter, setSecondaryTagFilter,
    toggleExpanded, expandAll, collapseAll,
    getFilteredItems, getPrimaryTags, getSecondaryTags,
  } = useFaqStore();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FaqItem | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [lockWarning, setLockWarning] = useState<string | null>(null);
  const [sortError, setSortError] = useState<string | null>(null);

  const { checkLock, acquireLock, releaseLock, lockExpiring, refreshActivity } = useEditingLock();

  useEffect(() => {
    fetchItems();
    fetchTags();
    fetchTagOrders();
  }, [fetchItems, fetchTags, fetchTagOrders]);

  const filteredItems = getFilteredItems();
  const activeSortTagId = secondaryTagFilter ?? primaryTagFilter;
  const primaryTags = getPrimaryTags();
  const secondaryTags = getSecondaryTags();
  const allTags = [...primaryTags, ...secondaryTags];

  function handleEdit(item: FaqItem) {
    const lockInfo = checkLock(item.id);
    if (lockInfo.locked) {
      setLockWarning(`${lockInfo.lockedBy} 正在编辑此文章，请稍后再试`);
      return;
    }
    if (!acquireLock(item.id)) {
      setLockWarning('无法获取编辑锁，请稍后再试');
      return;
    }
    setEditingItem(item);
    setEditorOpen(true);
  }

  function handleCreate() {
    setEditingItem(null);
    setEditorOpen(true);
  }

  function handleEditorClose() {
    releaseLock();
    setEditorOpen(false);
    setEditingItem(null);
  }

  async function handleDelete(id: string) {
    try {
      await deleteItem(id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }

  async function handleMoveInActiveTag(itemId: string, direction: 'up' | 'down') {
    if (!activeSortTagId) return;
    const currentIndex = filteredItems.findIndex((item) => item.id === itemId);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= filteredItems.length) return;

    const nextIds = filteredItems.map((item) => item.id);
    [nextIds[currentIndex], nextIds[targetIndex]] = [nextIds[targetIndex], nextIds[currentIndex]];

    setSortError(null);
    try {
      await reorderItemsInTag(activeSortTagId, nextIds);
    } catch (error) {
      console.error('reorderItemsInTag failed:', error);
      setSortError('排序保存失败，请稍后重试');
    }
  }

  function extractText(content: string | null, maxLen = 120, format?: string): string {
    const full = faqAnswerToPlainText(content, format);
    return full.length > maxLen ? full.slice(0, maxLen) + '...' : full;
  }

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {/* 标题行 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">FAQ 管理</h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">管理常见问题与回答 · 共 {items.length} 条</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { fetchItems(); fetchTags(); fetchTagOrders(); }}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all" title="刷新">
            <RefreshCw size={16} />
          </button>
          <button onClick={handleCreate}
            className="flex items-center gap-1.5 px-4 py-2 bg-sky-500 text-white text-sm font-semibold rounded-xl hover:bg-sky-600 transition-all shadow-sm">
            <Plus size={16} /> 新建 FAQ
          </button>
        </div>
      </div>

      {/* 搜索 + 筛选栏 */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
        <div className="relative w-full sm:flex-1 sm:min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索问题..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all" />
        </div>

        {/* 一级标签筛选 */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
          <Filter size={14} className="text-slate-400" />
          <button onClick={() => setPrimaryTagFilter(null)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
              !primaryTagFilter ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}>全部</button>
          {primaryTags.map((t) => (
            <button key={t.id} onClick={() => setPrimaryTagFilter(primaryTagFilter === t.id ? null : t.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                primaryTagFilter === t.id ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}>{t.name}</button>
          ))}
        </div>

        {/* 二级标签筛选 */}
        {secondaryTags.length > 0 && (
          <select value={secondaryTagFilter || ''} onChange={(e) => setSecondaryTagFilter(e.target.value || null)}
            className="px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs text-slate-600 outline-none">
            <option value="">所有系统</option>
            {secondaryTags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}

        {/* 展开/折叠 */}
        <div className="flex items-center gap-1 ml-auto shrink-0">
          {viewMode === 'list' && (
            <>
              <button onClick={expandAll} className="px-2 py-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors">展开全部</button>
              <span className="text-slate-300">|</span>
              <button onClick={collapseAll} className="px-2 py-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors">折叠全部</button>
              <div className="w-px h-4 bg-slate-200 mx-1" />
            </>
          )}
          <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg ${viewMode === 'list' ? 'bg-sky-50 text-sky-600' : 'text-slate-400'}`}><ListIcon size={14} /></button>
          <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-lg ${viewMode === 'grid' ? 'bg-sky-50 text-sky-600' : 'text-slate-400'}`}><LayoutGrid size={14} /></button>
        </div>
      </div>

      {/* 加载/错误 */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-sky-500" size={28} />
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 mb-4">
          {error}
        </div>
      )}

      {sortError && (
        <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-sm text-rose-600 mb-4">
          {sortError}
        </div>
      )}

      {/* FAQ 列表 */}
      {!loading && filteredItems.length === 0 && (
        <div className="text-center py-20">
          <div className="text-5xl mb-4">📋</div>
          <p className="text-slate-400 text-sm">
            {searchQuery || primaryTagFilter ? '没有找到匹配的 FAQ' : '还没有 FAQ 条目，点击上方按钮创建'}
          </p>
        </div>
      )}

      {!loading && filteredItems.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredItems.map((item) => (
              <div key={item.id} className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
                onClick={() => handleEdit(item)}
              >
                {/* 封面图 */}
                <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-sky-400 to-indigo-500">
                  {item.cover_image ? (
                    <img src={item.cover_image} alt={item.question} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-white/60 text-5xl">❓</span>
                    </div>
                  )}

                  {/* 发布状态 */}
                  <div className="absolute top-3 right-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 backdrop-blur-sm text-white text-[11px] font-medium rounded-lg ${
                      item.is_published ? 'bg-emerald-500/70' : 'bg-slate-500/70'
                    }`}>
                      {item.is_published ? '已发布' : '草稿'}
                    </span>
                  </div>

                  {/* hover 遮罩 */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                      <span className="flex items-center gap-1.5 px-4 py-2 bg-white rounded-xl text-sm font-medium text-slate-700 shadow-sm">
                        <Edit3 size={14} />
                        编辑
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm(deleteConfirm === item.id ? null : item.id); }}
                        className="flex items-center gap-1 px-3 py-2 bg-white/90 rounded-xl text-sm font-medium text-red-500 shadow-sm hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* 删除确认 */}
                  {deleteConfirm === item.id && (
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-10" onClick={(e) => e.stopPropagation()}>
                      <div className="bg-white rounded-xl p-4 shadow-lg text-center">
                        <p className="text-sm text-slate-600 mb-3">确认删除此 FAQ？</p>
                        <div className="flex gap-2 justify-center">
                          <button onClick={() => handleDelete(item.id)} className="px-4 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg">确认</button>
                          <button onClick={() => setDeleteConfirm(null)} className="px-4 py-1.5 bg-slate-100 text-slate-500 text-xs font-medium rounded-lg">取消</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 内容 */}
                <div className="flex-1 flex flex-col p-5">
                  <h3 className="text-[15px] font-semibold text-slate-700 mb-2 line-clamp-2 group-hover:text-sky-600 transition-colors duration-300">
                    {item.question}
                  </h3>

                  {item.answer && (
                    <p className="text-sm text-slate-400 line-clamp-2 mb-4 flex-1">
                      {extractText(item.answer, 100, item.answer_format)}
                    </p>
                  )}

                  {/* 底部信息 */}
                  <div className="flex items-center justify-between text-xs text-slate-400 mt-auto pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      {faqTagLabels(item, allTags, 1).map((t) => (
                        <span key={t} className="px-2 py-0.5 bg-sky-50 text-sky-600 rounded-md font-medium">{t}</span>
                      ))}
                      {faqTagLabels(item, allTags, 2).map((t) => (
                        <span key={t} className="px-2 py-0.5 bg-purple-50 text-purple-500 rounded-md font-medium">{t}</span>
                      ))}
                    </div>
                    <span className="flex items-center gap-1">
                      <Eye size={12} /> {item.view_count}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredItems.length > 0 && viewMode === 'list' && (
          <div className="space-y-2">
            {filteredItems.map((item, index) => {
              const isExpanded = expandedIds.has(item.id);
              const sortPosition = activeSortTagId ? index + 1 : null;
              return (
                <div key={item.id} id={`faq-admin-item-${item.id}`}
                  className="group bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:shadow-sm transition-all overflow-hidden scroll-mt-12">
                  {/* 列表头 */}
                  <div className="flex items-start gap-2 sm:gap-3 px-3 sm:px-5 py-3 sm:py-4 cursor-pointer" onClick={() => {
                    const isExpanding = !expandedIds.has(item.id);
                    toggleExpanded(item.id);
                    if (isExpanding) {
                      requestAnimationFrame(() => {
                        document.getElementById(`faq-admin-item-${item.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      });
                    }
                  }}>
                    <button className="mt-0.5 text-slate-400 transition-transform" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)' }}>
                      <ChevronRight size={16} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-block w-2 h-2 rounded-full ${item.is_published ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                        <span className="w-4 h-4 rounded bg-sky-500 text-white text-[9px] font-bold flex items-center justify-center shrink-0">Q</span>
                        <h3 className="font-semibold text-slate-700 text-sm truncate">{item.question}</h3>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        {faqTagLabels(item, allTags, 1).map((t) => (
                          <span key={t} className="px-2 py-0.5 bg-sky-50 text-sky-600 rounded">{t}</span>
                        ))}
                        {faqTagLabels(item, allTags, 2).map((t) => (
                          <span key={t} className="px-2 py-0.5 bg-purple-50 text-purple-500 rounded">{t}</span>
                        ))}
                        {item.cover_image && <span className="text-amber-500">🖼</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 self-center" onClick={(e) => e.stopPropagation()}>
                      {/* 操作按钮 */}
                      <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100">
                        <button onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                          className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-all" title="编辑">
                          <Edit3 size={14} />
                        </button>
                        {deleteConfirm === item.id ? (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => handleDelete(item.id)} className="px-2 py-1 bg-red-500 text-white text-[10px] rounded-lg">确认删除</button>
                            <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 bg-slate-100 text-slate-500 text-[10px] rounded-lg">取消</button>
                          </div>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(item.id); }}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="删除">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                      {activeSortTagId && sortPosition !== null && (
                        <div
                          className="flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-1.5 py-1 text-[11px] font-semibold text-emerald-700 shadow-sm shadow-emerald-100/70 transition hover:border-emerald-300 hover:bg-emerald-100/70"
                          title={`当前标签排序第 ${sortPosition} 位`}
                        >
                          <span className="min-w-6 text-center tabular-nums">#{sortPosition}</span>
                          <button
                            type="button"
                            disabled={index === 0}
                            title="上移"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveInActiveTag(item.id, 'up');
                            }}
                            className="flex h-5 w-5 items-center justify-center rounded-md text-emerald-600 transition enabled:hover:bg-white enabled:hover:text-emerald-800 disabled:text-emerald-200"
                          >
                            <ChevronUp size={12} />
                          </button>
                          <button
                            type="button"
                            disabled={index === filteredItems.length - 1}
                            title="下移"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveInActiveTag(item.id, 'down');
                            }}
                            className="flex h-5 w-5 items-center justify-center rounded-md text-emerald-600 transition enabled:hover:bg-white enabled:hover:text-emerald-800 disabled:text-emerald-200"
                          >
                            <ChevronDown size={12} />
                          </button>
                        </div>
                      )}
                      <span className="flex items-center gap-1 text-[11px] text-slate-400"><Eye size={12} /> {item.view_count}</span>
                    </div>
                  </div>

                  {/* 展开内容 */}
                  {isExpanded && (
                    <FaqExpandedView item={item} isAdmin onEdit={handleEdit} />
                  )}
                </div>
              );
            })}
          </div>
        )}

      {/* 编辑器弹窗 */}
      <FaqEditorModal
        isOpen={editorOpen}
        onClose={handleEditorClose}
        editingItem={editingItem}
        lockExpiring={lockExpiring}
        onRefreshActivity={refreshActivity}
      />

      {/* 编辑锁冲突提示 */}
      {lockWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setLockWarning(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-sm mx-4 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <Lock size={22} className="text-amber-500" />
            </div>
            <h3 className="text-base font-bold text-slate-800 mb-1">文章编辑中</h3>
            <p className="text-sm text-slate-500 mb-4">{lockWarning}</p>
            <button
              onClick={() => setLockWarning(null)}
              className="px-6 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-200 transition-all"
            >知道了</button>
          </div>
        </div>
      )}
    </div>
  );
}
